import { useQueryScheduledVersion } from '@ben/columbia/hooks/use-query-scheduled-version';
import Banner from '@ben/orion/banner';
import Button, { ButtonVariant } from '@ben/orion/deprecated-button';
import Tabs from '@ben/orion/tabs';
import Typography, { TypographyVariant } from '@ben/orion/typography';
import { getDateInLocalTimezone } from '@ben/orion/utils/date';
import type { IModularBenefitDetail } from '@ben/shared/types/columbia';
import { format } from 'date-fns';
import { memo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import styled, { css } from 'styled-components';
import EditRenewalDateButton from '../edit-renewal-date-button';

export interface BenefitVersionBannerProps {
  benefit?: IModularBenefitDetail;
  benefitId?: string;
  versionId?: string;
  isScheduledVersion?: boolean;
  onClickDelete: () => void;
}

const BenefitVersionBanner = ({
  benefit,
  benefitId,
  versionId,
  isScheduledVersion,
  onClickDelete,
}: BenefitVersionBannerProps) => {
  const location = useLocation();
  const navigate = useNavigate();

  const { data: scheduledVersion } = useQueryScheduledVersion(benefitId);

  if (!scheduledVersion?.enrollableBenefit) {
    return false;
  }

  const startDate = getDateInLocalTimezone(
    scheduledVersion?.enrollableBenefit.scheduledRenewalDate
  );
  const formattedStartDate = startDate ? format(startDate, 'dd/MM/yyyy') : '';

  return (
    <>
      <StickyTabs
        value={location.pathname}
        onChange={navigate}
        id="benefit-version-tabs"
      >
        <Tabs.List>
          <Tabs.Tab value={`/benefits/${benefitId}/`}>Active benefit</Tabs.Tab>
          <Tabs.Tab
            value={`/benefits/${benefitId}/version/${scheduledVersion?.enrollableBenefit.id}/`}
          >
            Scheduled version
            {formattedStartDate && (
              <Badge variant={TypographyVariant.labelSm}>
                Start date: {formattedStartDate}
              </Badge>
            )}
          </Tabs.Tab>
        </Tabs.List>
      </StickyTabs>
      {scheduledVersion?.enrollableBenefit && !isScheduledVersion && (
        <Banner
          title="There is a more recent version of this benefit."
          message="Any changes you make here will not be reflected in the scheduled version. You’ll need to update the scheduled version separately."
        />
      )}
      {scheduledVersion?.enrollableBenefit && isScheduledVersion && (
        <Banner
          title="This is a scheduled version of the benefit"
          rightSection={
            <ButtonWrapper>
              {benefit && (
                <EditRenewalDateButton
                  benefit={benefit}
                  benefitId={benefitId}
                  versionId={versionId}
                />
              )}
              <Button
                label="Delete version"
                variant={ButtonVariant.error}
                onClick={onClickDelete}
                width="auto"
              />
            </ButtonWrapper>
          }
        >
          <Typography variant={TypographyVariant.bodyMd1}>
            All changes will take effect on the scheduled renewal date (
            <strong>{formattedStartDate}</strong>).
            <br />
            <strong>Important:</strong> Once the renewal window begins, it will
            not be possible to edit or delete this benefit version.
          </Typography>
        </Banner>
      )}
    </>
  );
};

export default memo(BenefitVersionBanner);

const StickyTabs = styled(Tabs)`
  ${({ theme }) => css`
    position: sticky;
    top: 0;
    z-index: 10;
    background-color: ${theme.palette.neutral['00']};
    padding-top: ${theme.spacing(2.5)};
    margin-top: ${theme.spacing(-2.5)};
  `}
`;

const Badge = styled(Typography)`
  ${({ theme }) => css`
    display: inline-block;
    background-color: ${theme.palette.primary.main};
    color: ${theme.palette.neutral['00']};
    padding: ${theme.spacing(0.25, 0.75)};
    border-radius: ${theme.borderRadius.sm};
    margin-left: ${theme.spacing(1)};
  `}
`;

const ButtonWrapper = styled(Typography)`
  ${({ theme }) => css`
    display: flex;
    gap: ${theme.spacing(2)};
  `}
`;
